import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Divider, FormControlLabel,
  IconButton, LinearProgress, Link as MuiLink, List, ListItemButton,
  ListItemText, MenuItem, Stack, Switch, Tab, Tabs, TextField,
  Tooltip, Typography, alpha,
} from "@mui/material";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../../config";
import {
  useGetDocumentationQuery, useGetDocArticleQuery, useGetOnboardingQuery,
  useCreateDocCategoryMutation, useCreateDocArticleMutation, useUpdateDocArticleMutation,
  useDeleteDocArticleMutation,
} from "../../../Api/platformApi";
import {
  AppPage, PageHeader, CardSurface, EmptyState, ActionButton,
  SlidePanel, FormCard, SearchBox, ConfirmDialog,
} from "../../../Components/ui";
import { NAVY, GOLD, TEXT_SECONDARY, BORDER } from "../../../theme/tokens";
import Markdown from "./Markdown";

/* ─── constants ─── */
const AUDIENCE_LABEL: Record<string, string> = { customer: "Help Center", admin: "Admin Documentation" };
const STORAGE_BASE = API_URL.replace(/\/api\/?$/, "") + "/storage/";
const CODE_BG = "#0f172a";

const blankArticle = { id: null, category_id: "", title: "", excerpt: "", content: "", is_published: true };

/* ─── CodeBlock ─── */
const CodeBlock: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="pre"
    sx={{
      background: CODE_BG,
      color: "#e2e8f0",
      fontFamily: "'Fira Code', 'Cascadia Code', 'Courier New', monospace",
      fontSize: 13,
      lineHeight: 1.65,
      borderRadius: "10px",
      p: 2.5,
      my: 2,
      overflowX: "auto",
      border: `1px solid ${alpha(GOLD, 0.15)}`,
      boxShadow: `0 4px 16px ${alpha("#000", 0.3)}`,
      "& code": { background: "none", p: 0, color: "inherit" },
    }}
  >
    {children}
  </Box>
);

/* ─── Main Page ─── */
const Documentation: React.FC = () => {
  const navigate = useNavigate();
  const { data: docData, isLoading } = useGetDocumentationQuery();
  const { data: onboardingData } = useGetOnboardingQuery();

  const canEdit: boolean = docData?.can_edit ?? false;
  const categories: any[] = docData?.data ?? [];

  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState<number | null>(null);

  // Default to the first article once the tree loads.
  useEffect(() => {
    if (activeId == null && categories.length) {
      const first = categories.find((c) => c.articles?.length)?.articles?.[0];
      if (first) setActiveId(first.id);
    }
  }, [categories, activeId]);

  const { data: articleData, isFetching: articleLoading } = useGetDocArticleQuery(activeId as number, { skip: activeId == null });
  const article = articleData?.data;

  // Group categories by audience for the two-section nav.
  const grouped = useMemo(() => {
    const g: Record<string, any[]> = {};
    const q = search.trim().toLowerCase();
    categories.forEach((c) => {
      const arts = (c.articles ?? []).filter((a: any) =>
        !q || a.title.toLowerCase().includes(q) || (a.excerpt ?? "").toLowerCase().includes(q));
      if (!q || arts.length) {
        (g[c.audience] = g[c.audience] || []).push({ ...c, _articles: arts });
      }
    });
    return g;
  }, [categories, search]);

  const isGettingStarted = useMemo(() => {
    const cat = categories.find((c) => c.articles?.some((a: any) => a.id === activeId));
    return cat?.slug === "getting-started";
  }, [categories, activeId]);

  if (isLoading) {
    return (
      <AppPage
        disablePaper
        header={
          <PageHeader
            icon={<MenuBookRoundedIcon />}
            title="Documentation"
            subtitle="Guides, API reference and SDKs"
          />
        }
      >
        <CardSurface noHover sx={{ p: 8, textAlign: "center" }}>
          <CircularProgress sx={{ color: NAVY }} />
        </CardSurface>
      </AppPage>
    );
  }

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<MenuBookRoundedIcon />}
          title="Documentation"
          subtitle="Guides, API reference and SDKs"
          actions={canEdit && <DocAdminBar categories={categories} onSelect={setActiveId} />}
        />
      }
    >
      <Stack direction={{ xs: "column", md: "row" }} spacing={2.5} alignItems="flex-start">

        {/* ── Left sidebar ── */}
        <CardSurface
          noHover
          sx={{
            width: { xs: "100%", md: 280 },
            flexShrink: 0,
            position: { md: "sticky" },
            top: 16,
            maxHeight: { md: "calc(100vh - 120px)" },
            overflowY: "auto",
            p: 2,
          }}
        >
          {/* Sidebar header */}
          <Box sx={{ mb: 2 }}>
            <SearchBox
              value={search}
              onChange={setSearch}
              placeholder="Search docs…"
              width="100%"
            />
          </Box>

          {Object.keys(grouped).length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2, textAlign: "center" }}>
              No matching articles.
            </Typography>
          )}

          {Object.entries(grouped).map(([audience, cats]) => (
            <Box key={audience} mb={2}>
              {/* Audience label */}
              <Box
                sx={{
                  px: 1,
                  py: 0.5,
                  mb: 1,
                  borderRadius: "8px",
                  bgcolor: alpha(NAVY, 0.06),
                  borderLeft: `3px solid ${GOLD}`,
                }}
              >
                <Typography
                  variant="overline"
                  sx={{ fontSize: 10, fontWeight: 800, color: NAVY, letterSpacing: "0.08em" }}
                >
                  {AUDIENCE_LABEL[audience] ?? audience}
                </Typography>
              </Box>

              {cats.map((c) => (
                <Box key={c.id} mb={1.5}>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ px: 1, pt: 0.5, pb: 0.25 }}>
                    <FolderOpenRoundedIcon sx={{ fontSize: 14, color: TEXT_SECONDARY }} />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: NAVY, letterSpacing: "0.02em", textTransform: "uppercase", fontSize: 10.5 }}
                    >
                      {c.title}
                    </Typography>
                  </Stack>

                  <List dense disablePadding>
                    {c._articles.map((a: any) => (
                      <ListItemButton
                        key={a.id}
                        selected={a.id === activeId}
                        onClick={() => setActiveId(a.id)}
                        sx={{
                          borderRadius: "8px",
                          py: 0.6,
                          px: 1.25,
                          mb: 0.25,
                          transition: "all 0.15s",
                          "&.Mui-selected": {
                            bgcolor: alpha(NAVY, 0.08),
                            borderLeft: `3px solid ${GOLD}`,
                            pl: "7px",
                            "& .MuiListItemText-primary": { color: NAVY, fontWeight: 700 },
                          },
                          "&:hover": { bgcolor: alpha(NAVY, 0.04) },
                        }}
                      >
                        <ListItemText
                          primary={a.title}
                          primaryTypographyProps={{ variant: "body2", noWrap: true, fontSize: 13 }}
                        />
                        {!a.is_published && (
                          <Chip
                            size="small"
                            label="draft"
                            sx={{
                              height: 18,
                              fontSize: 10,
                              bgcolor: alpha(GOLD, 0.15),
                              color: "#9a7a00",
                              fontWeight: 700,
                              border: `1px solid ${alpha(GOLD, 0.3)}`,
                            }}
                          />
                        )}
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              ))}
            </Box>
          ))}
        </CardSurface>

        {/* ── Right content ── */}
        <Box flexGrow={1} width="100%" minWidth={0}>

          {/* Onboarding widget */}
          {isGettingStarted && onboardingData?.data && (
            <OnboardingWidget data={onboardingData.data} onNavigate={navigate} />
          )}

          {/* Article panel */}
          <CardSurface noHover sx={{ p: { xs: 2.5, md: 4 }, minHeight: 360 }}>
            {articleLoading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

            {article ? (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5} gap={1}>
                  <Box>
                    <Typography fontSize={26} fontWeight={800} color={NAVY} letterSpacing="-0.02em" lineHeight={1.2}>
                      {article.title}
                    </Typography>
                    {article.excerpt && (
                      <Typography color="text.secondary" fontSize={14} mt={0.75}>
                        {article.excerpt}
                      </Typography>
                    )}
                  </Box>
                  {canEdit && (
                    <DocArticleActions
                      article={article}
                      categories={categories}
                      onDeleted={() => setActiveId(null)}
                    />
                  )}
                </Stack>

                <Divider sx={{ mb: 3, borderColor: BORDER }} />

                {/* Markdown article body with CodeBlock override */}
                <Box
                  sx={{
                    "& pre": {
                      background: `${CODE_BG} !important`,
                      color: "#e2e8f0",
                      fontFamily: "'Fira Code', 'Cascadia Code', 'Courier New', monospace",
                      fontSize: 13,
                      lineHeight: 1.65,
                      borderRadius: "10px",
                      p: "20px !important",
                      my: "16px",
                      overflowX: "auto",
                      border: `1px solid ${alpha(GOLD, 0.15)}`,
                      boxShadow: `0 4px 16px ${alpha("#000", 0.3)}`,
                    },
                    "& pre code": { background: "none !important", color: "inherit", padding: "0 !important" },
                    "& code": {
                      background: alpha(NAVY, 0.08),
                      color: NAVY,
                      borderRadius: "4px",
                      px: "5px",
                      py: "1px",
                      fontSize: "0.85em",
                      fontFamily: "'Fira Code', 'Courier New', monospace",
                    },
                    "& h1, & h2, & h3, & h4": { color: NAVY, fontWeight: 800, letterSpacing: "-0.01em", mt: 3, mb: 1 },
                    "& h1": { fontSize: 24 },
                    "& h2": { fontSize: 20, borderBottom: `2px solid ${BORDER}`, pb: 0.75 },
                    "& h3": { fontSize: 16 },
                    "& p": { lineHeight: 1.75, color: "#374151", mb: 1.5 },
                    "& a": { color: NAVY, fontWeight: 600, textDecoration: "underline" },
                    "& ul, & ol": { pl: 3, mb: 1.5, "& li": { mb: 0.5, color: "#374151" } },
                    "& blockquote": {
                      borderLeft: `4px solid ${GOLD}`,
                      ml: 0,
                      pl: 2,
                      py: 0.5,
                      bgcolor: alpha(GOLD, 0.05),
                      borderRadius: "0 8px 8px 0",
                      "& p": { color: "#6B7280", fontStyle: "italic", mb: 0 },
                    },
                    "& table": {
                      width: "100%",
                      borderCollapse: "collapse",
                      mb: 2,
                      "& th": { bgcolor: alpha(NAVY, 0.06), fontWeight: 700, color: NAVY, py: 1, px: 1.5, textAlign: "left", borderBottom: `2px solid ${BORDER}` },
                      "& td": { py: 1, px: 1.5, borderBottom: `1px solid ${BORDER}`, color: "#374151" },
                      "& tr:hover td": { bgcolor: alpha(NAVY, 0.02) },
                    },
                    "& img": { maxWidth: "100%", borderRadius: "10px", my: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" },
                    "& hr": { borderColor: BORDER, my: 3 },
                  }}
                >
                  <Markdown content={article.content} />
                </Box>
              </>
            ) : (
              !articleLoading && (
                <EmptyState
                  icon={<ArticleRoundedIcon />}
                  title="No article selected"
                  message="Choose an article from the left sidebar to start reading."
                />
              )
            )}
          </CardSurface>
        </Box>
      </Stack>
    </AppPage>
  );
};

/* ─── Onboarding checklist widget ─── */
const OnboardingWidget: React.FC<{ data: any; onNavigate: (p: string) => void }> = ({ data, onNavigate }) => (
  <CardSurface
    noHover
    sx={{
      p: 3,
      mb: 2.5,
      background: `linear-gradient(135deg, ${alpha(NAVY, 0.04)} 0%, ${alpha(GOLD, 0.04)} 100%)`,
      border: `1px solid ${alpha(GOLD, 0.2)}`,
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
      <Box>
        <Typography fontSize={16} fontWeight={800} color={NAVY}>Setup Completion</Typography>
        <Typography fontSize={12} color="text.secondary">Complete these steps to get started</Typography>
      </Box>
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, ${NAVY} 0%, #071521 100%)`,
          border: `2px solid ${alpha(GOLD, 0.5)}`,
          flexShrink: 0,
        }}
      >
        <Typography fontSize={14} fontWeight={800} color={GOLD}>{data.completion}%</Typography>
      </Box>
    </Stack>

    <LinearProgress
      variant="determinate"
      value={data.completion}
      sx={{
        height: 8,
        borderRadius: 4,
        mb: 2.5,
        bgcolor: alpha(NAVY, 0.08),
        "& .MuiLinearProgress-bar": {
          background: `linear-gradient(90deg, ${NAVY} 0%, ${GOLD} 100%)`,
          borderRadius: 4,
        },
      }}
    />

    <Stack spacing={0.75}>
      {(data.steps ?? []).map((s: any) => (
        <Stack
          key={s.key}
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{
            cursor: s.link ? "pointer" : "default",
            borderRadius: "8px",
            px: 1,
            py: 0.75,
            transition: "background 0.15s",
            "&:hover": { bgcolor: alpha(NAVY, 0.04) },
          }}
          onClick={() => s.link && onNavigate(s.link)}
        >
          {s.done
            ? <CheckCircleRoundedIcon sx={{ color: "#16A34A", fontSize: 18 }} />
            : <RadioButtonUncheckedIcon sx={{ color: TEXT_SECONDARY, fontSize: 18 }} />}
          <Typography
            variant="body2"
            fontSize={13}
            sx={{
              textDecoration: s.done ? "line-through" : "none",
              color: s.done ? "text.secondary" : "text.primary",
              flexGrow: 1,
            }}
          >
            {s.label}
          </Typography>
          {!s.done && s.link && (
            <MuiLink
              component="span"
              variant="caption"
              sx={{ color: NAVY, fontWeight: 700, fontSize: 11, whiteSpace: "nowrap" }}
            >
              Configure →
            </MuiLink>
          )}
        </Stack>
      ))}
    </Stack>
  </CardSurface>
);

/* ─── Super-admin authoring bar ─── */
const DocAdminBar: React.FC<{ categories: any[]; onSelect: (id: number) => void }> = ({ categories, onSelect }) => {
  const [catOpen, setCatOpen] = useState(false);
  const [artOpen, setArtOpen] = useState(false);
  return (
    <Stack direction="row" spacing={1}>
      <Button
        size="small"
        variant="outlined"
        startIcon={<AddRoundedIcon />}
        onClick={() => setCatOpen(true)}
        sx={{ borderRadius: "10px", borderColor: alpha(NAVY, 0.3), color: NAVY, fontWeight: 700 }}
      >
        New Section
      </Button>
      <ActionButton size="small" startIcon={<AddRoundedIcon />} onClick={() => setArtOpen(true)}>
        New Article
      </ActionButton>
      <CategoryPanel open={catOpen} onClose={() => setCatOpen(false)} />
      <ArticlePanel open={artOpen} onClose={() => setArtOpen(false)} categories={categories} onSaved={onSelect} />
    </Stack>
  );
};

/* ─── Article actions (edit / delete) ─── */
const DocArticleActions: React.FC<{ article: any; categories: any[]; onDeleted: () => void }> = ({ article, categories, onDeleted }) => {
  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteArticle] = useDeleteDocArticleMutation();

  return (
    <Stack direction="row" spacing={0.5} flexShrink={0}>
      <Tooltip title="Edit article">
        <IconButton
          size="small"
          onClick={() => setEditOpen(true)}
          sx={{
            borderRadius: "8px",
            border: `1px solid ${BORDER}`,
            "&:hover": { bgcolor: alpha(NAVY, 0.06), borderColor: alpha(NAVY, 0.2) },
          }}
        >
          <EditRoundedIcon fontSize="small" sx={{ color: NAVY }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete article">
        <IconButton
          size="small"
          onClick={() => setConfirmOpen(true)}
          sx={{
            borderRadius: "8px",
            border: "1px solid #fecaca",
            "&:hover": { bgcolor: "#fef2f2", borderColor: "#f87171" },
          }}
        >
          <DeleteRoundedIcon fontSize="small" sx={{ color: "#EF4444" }} />
        </IconButton>
      </Tooltip>

      <ArticlePanel open={editOpen} onClose={() => setEditOpen(false)} categories={categories} existing={article} />
      <ConfirmDialog
        open={confirmOpen}
        title="Delete this article?"
        message="This action is permanent and cannot be undone. The article will be removed immediately."
        confirmText="Delete Article"
        danger
        onClose={() => setConfirmOpen(false)}
        onConfirm={async () => {
          await deleteArticle(article.id).unwrap();
          setConfirmOpen(false);
          onDeleted();
        }}
      />
    </Stack>
  );
};

/* ─── Category create panel (SlidePanel) ─── */
const CategoryPanel: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const [create] = useCreateDocCategoryMutation();
  const [form, setForm] = useState({ audience: "customer", title: "", description: "" });

  const save = async () => {
    await create(form).unwrap();
    onClose();
    setForm({ audience: "customer", title: "", description: "" });
  };

  return (
    <SlidePanel
      open={open}
      onClose={onClose}
      title="New Section"
      subtitle="Add a documentation category"
      icon={<FolderOpenRoundedIcon />}
      footer={
        <>
          <Button variant="outlined" onClick={onClose} sx={{ borderRadius: "10px" }}>Cancel</Button>
          <ActionButton disabled={!form.title} onClick={save}>Create Section</ActionButton>
        </>
      }
    >
      <Stack spacing={2.5}>
        <FormCard title="Section Details" subtitle="Define the category audience and name" required>
          <Stack spacing={2}>
            <TextField
              select
              label="Audience"
              value={form.audience}
              onChange={(e) => setForm({ ...form, audience: e.target.value })}
              fullWidth
              size="small"
            >
              <MenuItem value="customer">Help Center (all users)</MenuItem>
              <MenuItem value="admin">Admin Documentation (super admin only)</MenuItem>
            </TextField>
            <TextField
              label="Title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              fullWidth
              size="small"
              required
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
              size="small"
              multiline
              rows={2}
            />
          </Stack>
        </FormCard>
      </Stack>
    </SlidePanel>
  );
};

/* ─── Article create/edit panel (SlidePanel) ─── */
const ArticlePanel: React.FC<{
  open: boolean;
  onClose: () => void;
  categories: any[];
  existing?: any;
  onSaved?: (id: number) => void;
}> = ({ open, onClose, categories, existing, onSaved }) => {
  const [createArticle] = useCreateDocArticleMutation();
  const [updateArticle] = useUpdateDocArticleMutation();
  const [form, setForm] = useState<any>(blankArticle);
  const [tab, setTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setForm(existing ? { ...blankArticle, ...existing } : blankArticle);
      setTab(0);
    }
  }, [open, existing]);

  const insertImage = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API_URL}/utility/upload`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      const path = res.data?.data;
      if (path) {
        const md = `\n![image](${STORAGE_BASE}${path})\n`;
        setForm((f: any) => ({ ...f, content: (f.content || "") + md }));
      }
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    const payload = {
      category_id: form.category_id,
      title: form.title,
      excerpt: form.excerpt || null,
      content: form.content,
      is_published: form.is_published,
    };
    let id = existing?.id;
    if (existing?.id) {
      await updateArticle({ id: existing.id, body: payload }).unwrap();
    } else {
      const res: any = await createArticle(payload).unwrap();
      id = res?.data?.id;
    }
    if (id && onSaved) onSaved(id);
    onClose();
  };

  const canSave = !!form.title && !!form.content && !!form.category_id;

  return (
    <SlidePanel
      open={open}
      onClose={onClose}
      title={existing ? "Edit Article" : "New Article"}
      subtitle={existing ? `Editing: ${existing.title}` : "Write a new documentation article"}
      icon={<ArticleRoundedIcon />}
      width={720}
      footer={
        <>
          <Button variant="outlined" onClick={onClose} sx={{ borderRadius: "10px" }}>Cancel</Button>
          <ActionButton disabled={!canSave} onClick={save}>
            {existing ? "Update Article" : "Create Article"}
          </ActionButton>
        </>
      }
    >
      <Stack spacing={2.5}>

        {/* Metadata */}
        <FormCard title="Article Details" subtitle="Category, title and summary" required>
          <Stack spacing={2}>
            <TextField
              select
              label="Section"
              value={form.category_id}
              onChange={(e) => setForm({ ...form, category_id: e.target.value })}
              fullWidth
              size="small"
              required
            >
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {(AUDIENCE_LABEL[c.audience] ?? c.audience) + " — " + c.title}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              fullWidth
              size="small"
              required
            />
            <TextField
              label="Excerpt / summary"
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              fullWidth
              size="small"
              multiline
              rows={2}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.is_published}
                  onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                  size="small"
                />
              }
              label={<Typography fontSize={13} fontWeight={600}>Published</Typography>}
            />
          </Stack>
        </FormCard>

        {/* Content editor */}
        <FormCard
          title="Content"
          subtitle="Write in Markdown — code blocks, images, tables supported"
          required
          headerAction={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) insertImage(f);
                  e.target.value = "";
                }}
              />
              <Button
                size="small"
                startIcon={<ImageRoundedIcon />}
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
                sx={{ borderRadius: "8px", fontSize: 12, fontWeight: 600 }}
              >
                {uploading ? "Uploading…" : "Insert Image"}
              </Button>
            </Box>
          }
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              mb: 1.5,
              minHeight: 36,
              "& .MuiTab-root": { minHeight: 36, fontSize: 13, fontWeight: 600, py: 0.5 },
              "& .MuiTabs-indicator": { bgcolor: NAVY },
              "& .Mui-selected": { color: `${NAVY} !important` },
            }}
          >
            <Tab label="Markdown" />
            <Tab label="Preview" />
          </Tabs>

          {tab === 0 ? (
            <TextField
              label="Content (Markdown)"
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              fullWidth
              multiline
              minRows={16}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": { fontFamily: "'Fira Code', 'Courier New', monospace", fontSize: 13 },
              }}
            />
          ) : (
            <Box
              sx={{
                border: `1px solid ${BORDER}`,
                borderRadius: "10px",
                p: 2.5,
                minHeight: 300,
                "& pre": {
                  background: CODE_BG,
                  color: "#e2e8f0",
                  fontFamily: "'Fira Code', 'Courier New', monospace",
                  fontSize: 13,
                  borderRadius: "10px",
                  p: "16px",
                  overflowX: "auto",
                  border: `1px solid ${alpha(GOLD, 0.15)}`,
                },
                "& pre code": { background: "none", color: "inherit" },
                "& code": { background: alpha(NAVY, 0.08), color: NAVY, borderRadius: "4px", px: "4px", fontSize: "0.85em" },
                "& h1, & h2, & h3": { color: NAVY, fontWeight: 800 },
                "& p": { lineHeight: 1.75 },
              }}
            >
              <Markdown content={form.content} />
            </Box>
          )}
        </FormCard>

      </Stack>
    </SlidePanel>
  );
};

export default Documentation;
